import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "./pages/Login";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Analytics from "./pages/Analytics";
import Default from "./pages/Default";
import Ecommerce from "./pages/Ecommerce";
import DigitalMarketing from "./pages/DigitalMarketing";
import HumanResources from "./pages/HumanResources";
import Application from "./pages/Application";
import Widgets from "./pages/Widgets";
import EcommerceSection from "./pages/EcommerceSection";
import Accounts from "./pages/Accounts";
import Users from "./pages/Users";
import Suppliers from "./pages/Suppliers";
import Requests from "./pages/Requests";
import Notifications from "./pages/Notifications";
import Products from "./pages/Products";
import Sites from "./pages/Sites";
import SiteDetails from "./pages/SiteDetails";
import Procurement from "./pages/Procurement";

const queryClient = new QueryClient();

const AppContent = () => (
  <AuthProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Index />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute allowedRoles={["admin", "director", "procurement"]}>
              <Products />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute allowedRoles={["admin", "director", "procurement"]}>
              <Suppliers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/requests"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "director",
                "quantity_surveyor",
                "procurement",
              ]}
            >
              <Requests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sites"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "director", "quantity_surveyor"]}
            >
              <Sites />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sites/:siteId"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "director", "quantity_surveyor"]}
            >
              <SiteDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Accounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/procurement"
          element={
            <ProtectedRoute allowedRoles={["procurement"]}>
              <Procurement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/default"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Default />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ecommerce"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Ecommerce />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/digital-marketing"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <DigitalMarketing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/human-resources"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <HumanResources />
            </ProtectedRoute>
          }
        />
        <Route
          path="/application"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Application />
            </ProtectedRoute>
          }
        />
        <Route
          path="/widgets"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <Widgets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ecommerce-section"
          element={
            <ProtectedRoute allowedRoles={["admin", "director"]}>
              <EcommerceSection />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </TooltipProvider>
  </AuthProvider>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
